import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SiFacebook } from 'react-icons/si';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: ROUTE_PATHS.HOME, label: 'Home' },
    { path: ROUTE_PATHS.SERVICES, label: 'Services' },
    { path: ROUTE_PATHS.ABOUT, label: 'About' },
    { path: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 w-full z-50 transition-all duration-200 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-sm border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-3">
              <div className="text-2xl font-bold tracking-tight text-foreground">
                <span className="text-accent">RICHMOND</span> LARGE DOG GROOMING
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-medium tracking-wide uppercase transition-colors ${
                      isActive
                        ? 'text-accent'
                        : 'text-muted-foreground hover:text-foreground'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <Link
              to={ROUTE_PATHS.CONTACT}
              className="hidden md:inline-flex items-center justify-center px-6 py-2.5 bg-accent text-accent-foreground font-semibold text-sm uppercase tracking-wide rounded-md hover:scale-[1.02] active:scale-[0.97] transition-all duration-200 shadow-md"
            >
              Book Now
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-foreground hover:text-accent transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={springPresets.gentle}
              className="md:hidden border-t border-border bg-background"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col space-y-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `text-base font-medium tracking-wide uppercase transition-colors ${
                        isActive
                          ? 'text-accent'
                          : 'text-muted-foreground hover:text-foreground'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to={ROUTE_PATHS.CONTACT}
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center px-6 py-3 bg-accent text-accent-foreground font-semibold text-sm uppercase tracking-wide rounded-md"
                >
                  Book Now
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="text-xl font-bold tracking-tight text-foreground mb-4">
                <span className="text-accent">RICHMOND</span> LARGE DOG GROOMING
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Professional grooming services for large and powerful dog breeds. We understand strength, we deliver excellence.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground mb-4">
                Quick Links
              </h3>
              <nav className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-sm text-muted-foreground hover:text-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground mb-4">
                Connect
              </h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/RichmondLargeDogGrooming"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-accent transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook size={20} />
                </a>
              </div>
              <div className="mt-6 text-sm text-muted-foreground">
                <p>6051 Livingstone Place</p>
                <p>Richmond, BC V7C 5J6</p>
                <p className="mt-2">778-680-9887</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© 2026 Richmond Large Dog Grooming. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
